import { BulbOutlined } from '@ant-design/icons';
import React from 'react';

import { DS } from '@/design-tokens';
import { SYSTEM_TIPS } from './tips';

export type SystemTipsPlacement = 'login' | 'loading' | 'footer';

interface SystemTipsProps {
  className?: string;
  intervalMs?: number;
  placement?: SystemTipsPlacement;
  style?: React.CSSProperties;
  tips?: readonly string[];
}

const PLACEMENT_STYLE: Record<SystemTipsPlacement, React.CSSProperties> = {
  login: {
    maxWidth: 408,
    minHeight: 36,
    margin: '0 auto',
    justifyContent: 'center',
    background: 'rgba(236, 253, 245, .74)',
    border: `1px solid ${DS.color.primary100}`,
    color: DS.color.primary700,
  },
  loading: {
    maxWidth: 560,
    minHeight: 40,
    margin: '0 auto',
    justifyContent: 'center',
    background: DS.color.panel,
    border: `1px solid ${DS.color.line}`,
    color: DS.color.textSoft,
    boxShadow: DS.shadow.sm,
  },
  footer: {
    maxWidth: 760,
    minHeight: 34,
    margin: '0 auto',
    justifyContent: 'center',
    background: DS.color.panel,
    border: `1px solid ${DS.color.lineHard}`,
    color: DS.color.text,
  },
};

const ICON_COLOR: Record<SystemTipsPlacement, string> = {
  login: DS.color.primary600,
  loading: DS.color.warn,
  footer: DS.color.primary600,
};

const DEFAULT_INTERVAL_MS = 6000;

const SystemTips: React.FC<SystemTipsProps> = ({
  className,
  intervalMs = DEFAULT_INTERVAL_MS,
  placement = 'footer',
  style,
  tips = SYSTEM_TIPS,
}) => {
  const safeTips = tips.length > 0 ? tips : SYSTEM_TIPS;
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    setIndex(0);
  }, [safeTips]);

  React.useEffect(() => {
    if (safeTips.length <= 1 || intervalMs <= 0) return undefined;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % safeTips.length);
    }, intervalMs);

    return () => window.clearInterval(timer);
  }, [safeTips, intervalMs]);

  const currentTip = safeTips[index] ?? safeTips[0];

  return (
    <div
      aria-live="polite"
      className={className}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 12px',
        borderRadius: DS.radius.md,
        fontFamily: DS.font.sans,
        fontSize: 12.5,
        lineHeight: 1.45,
        transition: DS.motion.base,
        ...PLACEMENT_STYLE[placement],
        ...style,
      }}
      title={currentTip}
    >
      <BulbOutlined
        aria-hidden="true"
        style={{
          color: ICON_COLOR[placement],
          flex: '0 0 auto',
          fontSize: 14,
        }}
      />
      <span
        style={{
          flex: '1 1 auto',
          minWidth: 0,
          overflow: 'visible',
          overflowWrap: 'anywhere',
          textAlign: 'center',
          textOverflow: 'clip',
          whiteSpace: 'normal',
        }}
      >
        <span
          style={{
            color: placement === 'footer' ? DS.color.inkSoft : undefined,
            fontWeight: 600,
          }}
        >
          操作提示：
        </span>
        {currentTip}
      </span>
    </div>
  );
};

export { SYSTEM_TIPS };
export default SystemTips;
